import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, CheckCircle2, Download, Lock, RotateCcw, Scissors, TrendingDown, TrendingUp, X } from 'lucide-react';
import api from '@/lib/api';
import { usePode } from '@/stores/authStore';
import Preloader from '@/components/Preloader';
import TelaProcessando from '@/components/TelaProcessando';
import LogoFornecedor from '@/components/LogoFornecedor';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import PassosDaCotacao from './PassosDaCotacao';
import type { Situacao } from './situacao';

type ItemDoPedido = {
  item_id: number;
  ordem: number;
  produto: string;
  embalagem: string | null;
  cores: Record<string, number>;
  quantidade_cotada_m: number;
  quantidade_m: number;
  preco_metro: number;
  valor: number;
};

type Pendente = {
  item_id: number;
  ordem: number;
  produto: string;
  embalagem: string | null;
  quantidade_m: number;
};

type Resumo = {
  confirmado: boolean;
  fornecedor: { id: number; apelido: string; nome: string; logo_url: string | null };
  rodada: { id: number; numero: number; nome: string };
  condicoes: { frete: string | null; pagamento: string | null; prazo_entrega_dias: number | null };
  itens: ItemDoPedido[];
  pendentes: Pendente[];
  quantidade_total_m: number;
  valor_total: number;
  historico: {
    pacote_inicial: number;
    melhor_pacote_inicial: number;
    pacote_fechado: number;
    ganho: number;
    ganho_pct: number;
    // A melhor outra proposta para o mesmo pedido; null sem outro fornecedor na rodada.
    concorrente: { id: number; apelido: string } | null;
    pacote_concorrente: number | null;
    ganho_concorrencia: number | null;
    ganho_concorrencia_pct: number | null;
  };
  fechado_em: string | null;
};

type Cotacao = {
  id: number;
  numero: number;
  descricao: string;
  status: string;
  origem: { id: number; numero: number } | null;
  rodadas: { id: number; numero: number; propostas: unknown[] }[];
  situacao: Situacao;
};

type FornecedorDaAnalise = {
  id: number;
  apelido: string;
  logo_url: string | null;
  excluido: boolean;
  pacote: number;
  falta: number;
  falta_pct: number;
  linhas_nao_cotadas: number;
};

const reais = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const preco = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const inteiro = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const metragem = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });
const pct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const dataCurta = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });

type Tom = 'ganho' | 'perda' | 'neutro';
// Abaixo de meio centavo é empate: nem verde nem vermelho.
const tomDe = (valor: number): Tom => (valor > 0.005 ? 'ganho' : valor < -0.005 ? 'perda' : 'neutro');

function mensagemDeErro(falha: unknown, padrao: string): string {
  const dados = (falha as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }).response?.data;
  return dados?.errors ? Object.values(dados.errors)[0][0] : (dados?.message ?? padrao);
}

/**
 * O fechamento do pacote. O comprador escolhe com quem fechar, confere o
 * pedido — o que não foi cotado fica de fora e pode ir para uma cotação nova —,
 * ajusta a quantidade à embalagem do fornecedor e confirma. Fechada, a tela
 * mostra o pedido, o ganho da negociação e a planilha para o Compumate.
 */
function Fechamento() {
  const { cotacaoId } = useParams();
  const [parametros, setParametros] = useSearchParams();
  const queryClient = useQueryClient();
  const podeFechar = usePode('cotacao.fechar');
  const podeSeparar = usePode('cotacao.equalizar');

  const fornecedorId = parametros.get('fornecedor') ? Number(parametros.get('fornecedor')) : null;
  // Só as quantidades que o comprador mexeu; o resto segue a cotação.
  const [quantidades, setQuantidades] = useState<Record<number, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  // Itens do pedido marcados para ir a uma cotação nova, negociada e fechada à parte.
  const [marcados, setMarcados] = useState<number[]>([]);
  const [confirmandoSeparacao, setConfirmandoSeparacao] = useState(false);
  const [separada, setSeparada] = useState<{ id: number; numero: number; itens: number } | null>(null);

  const { data: cotacao } = useQuery({
    queryKey: ['cotacao', cotacaoId],
    queryFn: async () => (await api.get<{ data: Cotacao }>(`/cotacoes/${cotacaoId}`)).data.data,
  });

  const fechada = cotacao?.status === 'fechada';
  const ultimaRodada = cotacao?.rodadas[cotacao.rodadas.length - 1]?.id ?? null;

  const { data: confirmado } = useQuery({
    queryKey: ['fechamento', cotacaoId],
    enabled: fechada,
    queryFn: async () => (await api.get<{ data: Resumo }>(`/cotacoes/${cotacaoId}/fechamento`)).data.data,
  });

  const { data: analise } = useQuery({
    queryKey: ['analise', ultimaRodada, []],
    enabled: cotacao !== undefined && !fechada && ultimaRodada !== null && fornecedorId === null,
    queryFn: async () =>
      (await api.get<{ data: { fornecedores: FornecedorDaAnalise[] } }>(`/rodadas/${ultimaRodada}/analise`)).data.data,
  });

  const {
    data: previa,
    error: erroDaPrevia,
    isLoading: carregandoPrevia,
  } = useQuery({
    queryKey: ['fechamento-previa', cotacaoId, fornecedorId],
    enabled: cotacao !== undefined && !fechada && fornecedorId !== null,
    retry: false,
    queryFn: async () =>
      (await api.post<{ data: Resumo }>(`/cotacoes/${cotacaoId}/fechamento/previa`, { fornecedor_id: fornecedorId })).data.data,
  });

  const resumo = fechada ? confirmado : previa;

  // Os valores vêm do servidor, que arredonda linha a linha e soma do mesmo
  // jeito que a listagem e a planilha; recalcular tudo aqui fazia o total da
  // cotação fechada diferir alguns centavos. Na prévia só a linha com
  // quantidade digitada é recalculada, e o total anda pela diferença dela.
  const itens = useMemo(
    () =>
      (resumo?.itens ?? []).map((item) => {
        const digitado = resumo?.confirmado ? undefined : quantidades[item.item_id];

        if (digitado === undefined) {
          return { ...item, invalida: false, confira: false };
        }

        const numero = digitado.trim() === '' ? Number.NaN : Number(digitado);
        // Mesma regra do servidor: a quantidade vale arredondada em centavos de metro.
        const invalida = !Number.isFinite(numero) || Math.round(numero * 100) <= 0;

        if (invalida) {
          return { ...item, invalida: true, confira: false };
        }

        // "1.200" no teclado brasileiro vira 1,2 m: longe demais da cotada, pede conferência.
        const confira = numero < item.quantidade_cotada_m * 0.5 || numero > item.quantidade_cotada_m * 2;
        const valor = numero === item.quantidade_m ? item.valor : Math.round(numero * item.preco_metro * 100) / 100;

        return { ...item, quantidade_m: numero, valor, invalida: false, confira };
      }),
    [resumo, quantidades],
  );
  const doServidor = new Map((resumo?.itens ?? []).map((item) => [item.item_id, item]));
  const valorTotal = resumo
    ? Math.round((resumo.valor_total + itens.reduce((soma, item) => soma + item.valor - (doServidor.get(item.item_id)?.valor ?? 0), 0)) * 100) / 100
    : 0;
  const metrosTotal = resumo
    ? resumo.quantidade_total_m + itens.reduce((soma, item) => soma + item.quantidade_m - (doServidor.get(item.item_id)?.quantidade_m ?? 0), 0)
    : 0;
  const temInvalida = itens.some((item) => item.invalida);

  const confirmar = useMutation({
    mutationFn: async () => {
      const ajustadas = Object.fromEntries(
        itens
          .filter((item) => !item.invalida && item.quantidade_m !== item.quantidade_cotada_m)
          .map((item) => [item.item_id, item.quantidade_m]),
      );

      return (
        await api.post<{ data: Resumo; message: string }>(`/cotacoes/${cotacaoId}/fechamento`, {
          fornecedor_id: fornecedorId,
          quantidades: ajustadas,
        })
      ).data;
    },
    onMutate: () => setErro(null),
    // A resposta já é o pedido confirmado: vai direto para o cache. Refazer a
    // prévia de uma cotação fechada devolvia 422 ("já está fechada"), que
    // piscava como erro, e esperar as consultas segurava a tela de processamento.
    onSuccess: (resposta) => {
      setQuantidades({});
      queryClient.setQueryData(['fechamento', cotacaoId], resposta.data);
      queryClient.setQueryData<Cotacao>(['cotacao', cotacaoId], (anterior) => (anterior ? { ...anterior, status: 'fechada' } : anterior));
      queryClient.removeQueries({ queryKey: ['fechamento-previa', cotacaoId] });
      void queryClient.invalidateQueries({ queryKey: ['cotacao', cotacaoId] });
      void queryClient.invalidateQueries({ queryKey: ['cotacoes'] });
    },
    onError: (falha) => setErro(mensagemDeErro(falha, 'Não foi possível fechar o pacote.')),
  });

  const reabrir = useMutation({
    mutationFn: async () => (await api.delete(`/cotacoes/${cotacaoId}/fechamento`)).data,
    onMutate: () => setErro(null),
    // A cotação sai de fechada antes de o pedido sair do cache: com a ordem
    // inversa a consulta do fechamento, ainda habilitada, iria ao servidor e
    // voltaria 404.
    onSuccess: () => {
      if (confirmado) setParametros({ fornecedor: String(confirmado.fornecedor.id) });
      queryClient.setQueryData<Cotacao>(['cotacao', cotacaoId], (anterior) =>
        anterior ? { ...anterior, status: 'em_negociacao' } : anterior,
      );
      queryClient.removeQueries({ queryKey: ['fechamento', cotacaoId] });
      void queryClient.invalidateQueries({ queryKey: ['cotacao', cotacaoId] });
      void queryClient.invalidateQueries({ queryKey: ['cotacoes'] });
      void queryClient.invalidateQueries({ queryKey: ['fechamento-previa', cotacaoId] });
    },
    onError: (falha) => setErro(mensagemDeErro(falha, 'Não foi possível reabrir o fechamento.')),
  });

  // Quem separa daqui está fechando: a tela fica no fechamento, a prévia volta
  // sem os itens separados e um aviso leva à cotação nova.
  const separar = useMutation({
    mutationFn: async (ids: number[]) =>
      (await api.post<{ data: { id: number; numero: number; itens: number } }>(`/cotacoes/${cotacaoId}/separacoes`, { itens: ids })).data
        .data,
    onMutate: () => {
      setErro(null);
      setSeparada(null);
    },
    onSuccess: (nova, ids) => {
      setMarcados([]);
      setConfirmandoSeparacao(false);
      // A quantidade digitada de um item separado não vale mais: ele saiu do pedido.
      setQuantidades((atual) => Object.fromEntries(Object.entries(atual).filter(([id]) => !ids.includes(Number(id)))));
      setSeparada(nova);
      // Reset, e não invalidate: a prévia antiga, com os itens que saíram, não
      // fica na tela enquanto a nova é montada.
      void queryClient.resetQueries({ queryKey: ['fechamento-previa', cotacaoId] });
      void queryClient.invalidateQueries({ queryKey: ['cotacao', cotacaoId] });
      void queryClient.invalidateQueries({ queryKey: ['cotacoes'] });
    },
    onError: (falha) => setErro(mensagemDeErro(falha, 'Não foi possível separar os itens.')),
  });

  const alternarItem = (id: number) =>
    setMarcados((atual) => (atual.includes(id) ? atual.filter((i) => i !== id) : [...atual, id]));
  // Marcar todos e desmarcar o que fica é mais rápido quando sai quase tudo.
  const todosMarcados = itens.length > 0 && marcados.length === itens.length;
  const alternarTodos = () => setMarcados(todosMarcados ? [] : itens.map((item) => item.item_id));
  const valorMarcado = itens.filter((item) => marcados.includes(item.item_id)).reduce((soma, item) => soma + item.valor, 0);
  // O servidor recusa esvaziar a cotação: sem pendentes, algum item do pedido precisa ficar.
  const separaTudo = todosMarcados && (resumo?.pendentes.length ?? 0) === 0;
  const separavel = podeSeparar && !fechada && resumo !== undefined && !resumo.confirmado;

  const baixarPlanilha = async () => {
    const resposta = await api.get(`/cotacoes/${cotacaoId}/fechamento/planilha`, { responseType: 'blob' });
    const url = URL.createObjectURL(resposta.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Cotacao-${cotacao?.numero ?? ''}-pedido-${(resumo?.fornecedor.apelido ?? '').toLowerCase()}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!cotacao) {
    return <Preloader telaCheia mensagem="Carregando cotação…" className="py-3xl" />;
  }

  const escolhendo = !fechada && fornecedorId === null;

  return (
    <div className="px-lg py-lg">
      {confirmar.isPending && <TelaProcessando titulo="Fechando o pacote…" detalhe="Gravando o pedido e o ganho da negociação." />}
      {separar.isPending && <TelaProcessando titulo="Separando os itens…" detalhe="Criando a cotação nova com os preços atuais." />}

      <PassosDaCotacao cotacao={cotacao} atual="fechamento" rodadaId={ultimaRodada} />

      <div className="mb-md flex flex-wrap items-start justify-between gap-md">
        <div className="min-w-0">
          <p className="text-[12px] font-bold tracking-wide text-accent uppercase">Fechamento</p>
          <h1 className="text-[18px] font-bold text-ink">
            <span className="mr-sm text-accent tabular-nums">Nº {cotacao.numero}</span>
            {cotacao.descricao}
          </h1>
          <p className="text-[13px] text-muted">
            {cotacao.origem && (
              <>
                Separada da{' '}
                <Link to={`/cotacoes/${cotacao.origem.id}/fechamento`} className="font-bold text-accent hover:underline">
                  Nº {cotacao.origem.numero}
                </Link>
              </>
            )}
          </p>
        </div>

        {/* Pedido fechado, o que o comprador faz em seguida é baixar a planilha
            (ou desfazer): as ações ficam no topo, sem descer as 59 linhas. */}
        {fechada && resumo?.confirmado && (
          <div className="flex flex-wrap items-center gap-sm">
            {podeFechar && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Reabrir desfaz o fechamento e volta a cotação para a negociação. Continuar?')) reabrir.mutate();
                }}
                disabled={reabrir.isPending}
                className="inline-flex items-center gap-xs rounded-md border border-border bg-surface px-md py-sm text-[13px] font-bold text-ink hover:border-accent"
              >
                <RotateCcw size={14} aria-hidden="true" />
                Reabrir
              </button>
            )}
            <button
              type="button"
              onClick={baixarPlanilha}
              className="inline-flex items-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white hover:bg-accent/90"
            >
              <Download size={15} aria-hidden="true" />
              Baixar planilha do pedido
            </button>
          </div>
        )}
      </div>

      {erro && (
        <p className="mb-md flex items-center gap-xs rounded-md border border-danger/30 bg-danger/5 px-md py-sm text-[13px] font-bold text-danger">
          <AlertTriangle size={14} aria-hidden="true" />
          {erro}
        </p>
      )}

      {separada && (
        <div className="mb-md flex items-start justify-between gap-sm rounded-md border border-success/30 bg-success/5 px-md py-sm text-[13px] text-ink">
          <p>
            <CheckCircle2 size={14} className="mr-xs inline align-[-2px] text-success" aria-hidden="true" />
            {separada.itens === 1 ? '1 item separado' : `${separada.itens} itens separados`} para a{' '}
            <Link to={`/cotacoes/${separada.id}/equalizacao`} className="font-bold text-accent hover:underline">
              Nº {separada.numero}
            </Link>
            . O pedido abaixo já está sem {separada.itens === 1 ? 'ele' : 'eles'}.
          </p>
          <button type="button" onClick={() => setSeparada(null)} aria-label="Fechar aviso" className="text-muted hover:text-ink">
            <X size={14} />
          </button>
        </div>
      )}

      {escolhendo ? (
        <EscolherFornecedor
          fornecedores={analise?.fornecedores ?? null}
          podeFechar={podeFechar}
          aoEscolher={(id) => setParametros({ fornecedor: String(id) })}
        />
      ) : !resumo ? (
        // Fechada, a prévia não vale mais: um erro dela aqui seria só o resto de
        // antes de confirmar. O que falta é o pedido gravado chegar.
        fechada ? (
          <Preloader telaCheia mensagem="Carregando o pedido…" className="py-3xl" />
        ) : erroDaPrevia ? (
          <div className="rounded-lg border border-border bg-surface px-lg py-lg text-[13px]">
            <p className="font-bold text-danger">{mensagemDeErro(erroDaPrevia, 'Não foi possível montar o pedido.')}</p>
            <button type="button" onClick={() => setParametros({})} className="mt-sm font-bold text-accent hover:underline">
              Escolher outro fornecedor
            </button>
          </div>
        ) : (
          <Preloader telaCheia={carregandoPrevia} mensagem="Montando o pedido…" className="py-3xl" />
        )
      ) : (
        <>
          <section className="mb-lg grid gap-md sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
            <Numero rotulo="Pacote inicial" valor={reais.format(resumo.historico.pacote_inicial)} detalhe={`da ${resumo.fornecedor.apelido} na cotação inicial`} />
            <Numero rotulo="Pacote fechado" valor={reais.format(resumo.historico.pacote_fechado)} detalhe={`na ${resumo.rodada.nome.toLowerCase()}`} />
            <GanhoDaNegociacao historico={resumo.historico} naCotacaoInicial={resumo.rodada.numero === 1} />
            <FrenteAsPropostas historico={resumo.historico} />
            <Numero
              rotulo="Melhor pacote inicial"
              valor={reais.format(resumo.historico.melhor_pacote_inicial)}
              detalhe={
                resumo.historico.pacote_fechado > resumo.historico.melhor_pacote_inicial
                  ? `fechado ${reais.format(resumo.historico.pacote_fechado - resumo.historico.melhor_pacote_inicial)} acima dele`
                  : 'fechado abaixo do melhor pacote inicial'
              }
            />
          </section>

          {/* Sem overflow-hidden: ele prenderia o cabeçalho fixo da tabela dentro da seção. */}
          <section className="mb-lg rounded-lg border border-border bg-surface">
            <header className="flex flex-wrap items-center justify-between gap-sm border-b border-border px-md py-sm">
              <div>
                <h2 className="inline-flex flex-wrap items-center gap-sm text-[14px] font-bold text-ink">
                  <LogoFornecedor logoUrl={resumo.fornecedor.logo_url} apelido={resumo.fornecedor.apelido} tamanho="sm" />
                  {resumo.confirmado ? 'Pedido' : 'Prévia do pedido'} — {resumo.fornecedor.nome}
                </h2>
                <p className="text-[12px] text-muted">
                  Frete {resumo.condicoes.frete ?? '—'} · Pagamento {resumo.condicoes.pagamento ?? '—'} · Entrega{' '}
                  {resumo.condicoes.prazo_entrega_dias ? `${resumo.condicoes.prazo_entrega_dias} dias` : '—'}
                  {resumo.fechado_em && ` · fechado em ${dataCurta.format(new Date(resumo.fechado_em))}`}
                </p>
              </div>
              {!resumo.confirmado && (
                <button type="button" onClick={() => setParametros({})} className="text-[12px] font-bold text-accent hover:underline">
                  Trocar fornecedor
                </button>
              )}
            </header>

            {separavel && marcados.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-sm border-b border-border bg-accent/5 px-md py-sm">
                <span className="text-[12px] text-ink-soft">
                  <b className="tabular-nums">{marcados.length}</b> {marcados.length === 1 ? 'item marcado' : 'itens marcados'} ·{' '}
                  <span className="tabular-nums">{reais.format(valorMarcado)}</span> no pedido
                  {separaTudo && <span className="ml-xs font-bold text-warning">Desmarque ao menos um item para ficar no pedido.</span>}
                </span>
                <span className="flex items-center gap-sm">
                  <button type="button" onClick={() => setMarcados([])} className="text-[12px] text-muted hover:text-ink">
                    Limpar
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmandoSeparacao(true)}
                    disabled={separaTudo}
                    className="inline-flex items-center gap-xs rounded-md bg-accent px-md py-xs text-[12px] font-bold text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Scissors size={13} aria-hidden="true" />
                    Separar em nova cotação
                  </button>
                </span>
              </div>
            )}

            {/* Como na equalização: o cabeçalho gruda abaixo da barra do topo enquanto a
                página rola. Em tela estreita a tabela rola de lado e o cabeçalho não gruda —
                grudaria dentro da moldura, deslocado por cima das primeiras linhas. */}
            <div className="overflow-x-auto xl:overflow-visible">
              <table className="w-full text-[13px]">
                <thead className="z-20 bg-surface-subtle xl:sticky xl:top-[var(--altura-barra,54px)] text-[11px] uppercase tracking-wide text-ink-soft shadow-[inset_0_-1px_0_var(--color-border)]">
                  <tr>
                    {separavel && (
                      <th className="w-[1%] py-xs pl-sm">
                        <input
                          type="checkbox"
                          checked={todosMarcados}
                          ref={(caixa) => {
                            if (caixa) caixa.indeterminate = marcados.length > 0 && !todosMarcados;
                          }}
                          onChange={alternarTodos}
                          aria-label="Marcar todos os itens para separar"
                        />
                      </th>
                    )}
                    <th className="px-sm py-xs text-left">Produto</th>
                    <th className="px-sm py-xs text-right">Cotado</th>
                    <th className="px-sm py-xs text-right">No pedido</th>
                    <th className="px-sm py-xs text-right">Preço por metro</th>
                    <th className="px-sm py-xs text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((item) => (
                    <tr key={item.item_id} className={`border-t border-border ${marcados.includes(item.item_id) ? 'bg-accent/5' : ''}`}>
                      {separavel && (
                        <td className="py-xs pl-sm align-top">
                          <input
                            type="checkbox"
                            checked={marcados.includes(item.item_id)}
                            onChange={() => alternarItem(item.item_id)}
                            aria-label={`Marcar ${item.produto} para separar`}
                            className="mt-[3px]"
                          />
                        </td>
                      )}
                      <td className="px-sm py-xs">
                        <span className="block font-medium text-ink">{item.produto}</span>
                        <span className="text-[11px] text-muted">{item.embalagem}</span>
                        {Object.keys(item.cores).length > 0 && (
                          <span className="block text-[11px] text-muted">
                            {Object.entries(item.cores)
                              .map(([cor, metros]) => `${cor} ${inteiro.format(metros)} m`)
                              .join(' · ')}
                          </span>
                        )}
                      </td>
                      <td className="px-sm py-xs text-right tabular-nums text-muted">{inteiro.format(item.quantidade_cotada_m)} m</td>
                      <td className="px-sm py-xs text-right">
                        {resumo.confirmado ? (
                          <span className="tabular-nums">{inteiro.format(item.quantidade_m)} m</span>
                        ) : (
                          <>
                            <input
                              type="number"
                              min={0.01}
                              step="any"
                              value={quantidades[item.item_id] ?? String(item.quantidade_m)}
                              onChange={(evento) => setQuantidades((atual) => ({ ...atual, [item.item_id]: evento.target.value }))}
                              aria-label={`Quantidade no pedido de ${item.produto}, em metros`}
                              aria-invalid={item.invalida}
                              className={`w-[110px] rounded border px-xs py-[2px] text-right tabular-nums ${
                                item.invalida
                                  ? 'border-danger bg-danger/5'
                                  : item.quantidade_m !== item.quantidade_cotada_m
                                    ? 'border-accent bg-accent/5'
                                    : 'border-border bg-surface'
                              }`}
                            />
                            {item.invalida && <span className="mt-[2px] block text-[11px] font-bold text-danger">Quantidade inválida</span>}
                            {item.confira && (
                              <span className="mt-[2px] block text-[11px] font-bold text-warning">
                                Confira: {metragem.format(item.quantidade_m)} m
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-sm py-xs text-right tabular-nums">{preco.format(item.preco_metro)}</td>
                      <td className="px-sm py-xs text-right font-medium tabular-nums">{reais.format(item.valor)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-surface-subtle">
                  <tr className="border-t border-border font-bold">
                    {separavel && <td />}
                    <td className="px-sm py-xs text-ink">Total do pedido</td>
                    <td />
                    <td className="px-sm py-xs text-right tabular-nums">{inteiro.format(metrosTotal)} m</td>
                    <td />
                    <td className="px-sm py-xs text-right tabular-nums text-ink">{reais.format(valorTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {resumo.pendentes.length > 0 && (
            <section className="mb-lg rounded-lg border border-warning/40 bg-warning/5 px-md py-sm">
              <div className="flex flex-wrap items-start justify-between gap-sm">
                <div className="min-w-0">
                  <h2 className="text-[14px] font-bold text-ink">
                    Fora do pedido: {resumo.pendentes.length === 1 ? '1 item' : `${resumo.pendentes.length} itens`} que a{' '}
                    {resumo.fornecedor.apelido} não cotou
                  </h2>
                  <ul className="mt-xs text-[12px] text-ink-soft">
                    {resumo.pendentes.map((pendente) => (
                      <li key={pendente.item_id}>
                        {pendente.produto}
                        {pendente.embalagem && <span className="text-muted"> · {pendente.embalagem}</span>}
                        <span className="text-muted tabular-nums"> · {inteiro.format(pendente.quantidade_m)} m</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {podeSeparar && (
                  <button
                    type="button"
                    onClick={() => separar.mutate(resumo.pendentes.map((p) => p.item_id))}
                    disabled={separar.isPending}
                    className="inline-flex items-center gap-xs rounded-md border border-accent bg-surface px-md py-sm text-[13px] font-bold text-accent hover:bg-accent hover:text-white"
                  >
                    <Scissors size={14} aria-hidden="true" />
                    Separar em nova cotação
                  </button>
                )}
              </div>
            </section>
          )}

          <div className="flex flex-wrap items-center justify-end gap-sm">
            {resumo.confirmado ? (
              <span className="mr-auto inline-flex items-center gap-xs text-[13px] font-bold text-success">
                <Lock size={14} aria-hidden="true" />
                Cotação fechada com a {resumo.fornecedor.apelido}
              </span>
            ) : (
              <>
                {/* Campo inválido não pode virar a quantidade cotada em silêncio: o pedido sairia cheio. */}
                {/* Fechar com quem não é o mais barato vale um aviso ao lado do botão, não só no card lá em cima. */}
                {resumo.historico.concorrente && tomDe(resumo.historico.ganho_concorrencia ?? 0) === 'perda' && (
                  <span className="mr-auto inline-flex items-center gap-xs text-[13px] font-bold text-danger">
                    <AlertTriangle size={14} aria-hidden="true" />
                    A {resumo.historico.concorrente.apelido} tem este pedido por{' '}
                    {reais.format(Math.abs(resumo.historico.ganho_concorrencia ?? 0))} a menos.
                  </span>
                )}
                {temInvalida && <span className="text-[13px] font-bold text-danger">Corrija as quantidades marcadas em vermelho.</span>}
                <button
                  type="button"
                  onClick={() => confirmar.mutate()}
                  disabled={!podeFechar || confirmar.isPending || temInvalida}
                  className="inline-flex items-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Check size={15} aria-hidden="true" />
                  Confirmar fechamento com a {resumo.fornecedor.apelido}
                </button>
              </>
            )}
          </div>
        </>
      )}

      <Dialog open={confirmandoSeparacao} onOpenChange={(aberto) => !separar.isPending && setConfirmandoSeparacao(aberto)}>
        <DialogContent className="max-w-[460px] p-lg">
          <DialogTitle>Separar {marcados.length === 1 ? '1 item' : `${marcados.length} itens`} em nova cotação?</DialogTitle>
          <DialogDescription className="mt-xs">
            Eles saem deste pedido e vão para uma cotação nova, com os preços da última rodada, que segue a negociação e o
            fechamento próprios. Quantidades ajustadas nesses itens se perdem.
          </DialogDescription>
          <div className="mt-lg flex justify-end gap-sm">
            <button
              type="button"
              onClick={() => setConfirmandoSeparacao(false)}
              className="rounded-md border border-border px-md py-sm text-[13px] font-bold text-ink hover:bg-surface-subtle"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => separar.mutate(marcados)}
              disabled={separar.isPending}
              className="inline-flex items-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white disabled:opacity-50"
            >
              <Scissors size={14} aria-hidden="true" />
              Separar
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const estiloDoTom: Record<Tom, { caixa: string; valor: string; icone: typeof TrendingUp | null }> = {
  ganho: { caixa: 'border-success bg-success/5', valor: 'text-success', icone: TrendingUp },
  perda: { caixa: 'border-danger bg-danger/5', valor: 'text-danger', icone: TrendingDown },
  neutro: { caixa: 'border-border bg-surface', valor: 'text-ink', icone: null },
};

function Numero({ rotulo, valor, detalhe, tom = 'neutro' }: { rotulo: string; valor: string; detalhe: string; tom?: Tom }) {
  const estilo = estiloDoTom[tom];
  const Icone = estilo.icone;

  return (
    <div className={`rounded-lg border p-md ${estilo.caixa}`}>
      <p className={`flex items-center gap-xs text-[12px] font-bold ${tom === 'neutro' ? 'text-muted' : estilo.valor}`}>
        {Icone && <Icone size={14} aria-hidden="true" />}
        {rotulo}
      </p>
      <p className={`mt-xs text-[19px] font-bold tabular-nums ${estilo.valor}`}>{valor}</p>
      <p className="text-[12px] text-muted">{detalhe}</p>
    </div>
  );
}

/** O que as rodadas tiraram do pacote do fornecedor escolhido, desde a cotação inicial. */
function GanhoDaNegociacao({ historico, naCotacaoInicial }: { historico: Resumo['historico']; naCotacaoInicial: boolean }) {
  const tom = tomDe(historico.ganho);

  if (tom === 'perda') {
    return (
      <Numero
        rotulo="Perda na negociação"
        valor={reais.format(-historico.ganho)}
        detalhe={`o pacote subiu ${pct(-historico.ganho_pct)} desde a cotação inicial`}
        tom="perda"
      />
    );
  }

  return (
    <Numero
      rotulo="Ganho da negociação"
      valor={reais.format(Math.max(historico.ganho, 0))}
      detalhe={
        tom === 'ganho'
          ? `${pct(historico.ganho_pct)} do pacote inicial`
          : naCotacaoInicial
            ? 'fechado na cotação inicial, sem rodada de negociação'
            : 'o preço não baixou nas rodadas'
      }
      tom={tom}
    />
  );
}

/**
 * O pacote fechado contra a melhor outra proposta para o mesmo pedido. Fechar
 * com quem não é o mais barato aparece em vermelho, como perda.
 */
function FrenteAsPropostas({ historico }: { historico: Resumo['historico'] }) {
  const { concorrente, ganho_concorrencia: diferenca, ganho_concorrencia_pct: percentual } = historico;

  if (!concorrente || diferenca === null || percentual === null) {
    return <Numero rotulo="Frente às propostas" valor="—" detalhe="sem outra proposta para comparar" />;
  }

  const tom = tomDe(diferenca);

  if (tom === 'neutro') {
    return <Numero rotulo="Frente às propostas" valor={reais.format(0)} detalhe={`empatado com a ${concorrente.apelido}`} />;
  }

  return tom === 'ganho' ? (
    <Numero
      rotulo="Ganho frente às propostas"
      valor={reais.format(diferenca)}
      detalhe={`${pct(percentual)} abaixo da ${concorrente.apelido}, a melhor outra proposta`}
      tom="ganho"
    />
  ) : (
    <Numero
      rotulo="Perda frente às propostas"
      valor={reais.format(-diferenca)}
      detalhe={`${pct(-percentual)} acima da ${concorrente.apelido}, a proposta mais barata`}
      tom="perda"
    />
  );
}

function EscolherFornecedor({
  fornecedores,
  podeFechar,
  aoEscolher,
}: {
  fornecedores: FornecedorDaAnalise[] | null;
  podeFechar: boolean;
  aoEscolher: (id: number) => void;
}) {
  if (fornecedores === null) {
    return <Preloader mensagem="Calculando os pacotes…" className="py-3xl" />;
  }

  // Do pacote mais barato para o mais caro: quem está mais perto do alvo vem primeiro.
  const ordenados = [...fornecedores].sort((a, b) => a.pacote - b.pacote);

  return (
    <section>
      <h2 className="mb-xs text-[14px] font-bold text-ink">Com quem fechar o pacote?</h2>
      <p className="mb-md text-[13px] text-muted">
        O pacote inteiro fecha com um fornecedor. O que ele não cotou fica fora do pedido e pode ir para uma cotação nova.
      </p>
      <div className="grid gap-md sm:grid-cols-2 lg:grid-cols-3">
        {ordenados.map((f) => (
          <div key={f.id} className="rounded-lg border border-border bg-surface p-md">
            <p className="inline-flex items-center gap-sm text-[14px] font-bold text-ink">
              <LogoFornecedor logoUrl={f.logo_url} apelido={f.apelido} />
              {f.apelido}
            </p>
            <p className="mt-xs text-[19px] font-bold tabular-nums text-ink">{reais.format(f.pacote)}</p>
            <p className="text-[12px] text-muted">
              {f.falta > 0.005 ? `${pct(f.falta_pct)} acima do melhor pacote` : 'no melhor pacote'}
              {f.linhas_nao_cotadas > 0 &&
                ` · ${f.linhas_nao_cotadas === 1 ? '1 item não cotado fica' : `${f.linhas_nao_cotadas} itens não cotados ficam`} de fora`}
            </p>
            <button
              type="button"
              onClick={() => aoEscolher(f.id)}
              disabled={!podeFechar}
              className="mt-md inline-flex w-full items-center justify-center rounded-md border border-accent bg-accent/10 px-sm py-sm text-[13px] font-bold text-accent hover:bg-accent hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              Ver o pedido com a {f.apelido}
            </button>
          </div>
        ))}
      </div>
      {!podeFechar && <p className="mt-sm text-[12px] text-muted">Fechar o pacote exige a permissão "Fechar pacote de compra".</p>}
    </section>
  );
}

/**
 * O link para a cotação de origem abre esta mesma tela; sem remontar, as
 * quantidades digitadas e o erro da cotação anterior ficariam na tela.
 */
export default function FechamentoPage() {
  const { cotacaoId } = useParams();

  return <Fechamento key={cotacaoId} />;
}
